import React from "react";

/**
 * Homepage Section 2 — The Problem.
 * Desktop: collage on the left, editorial copy on the right.
 * Mobile: copy first, then the collage.
 * The scattered "fragmentation" collage shows communication surfaces
 * (inbox skeleton, email, text thread, voicemail, DM, document chips)
 * connected by faint dashed paths.
 *
 * The collage is built on a fixed 900 x 800 design canvas and scaled down
 * responsively so proportions/overlaps stay identical at every width.
 */

/* ── collage pieces ───────────────────────────────────────────── */

const Card: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  soft?: boolean;
}> = ({ className = "", style, children, soft }) => (
  <div
    className={`absolute rounded-[22px] border border-[#EDF1F4] bg-white ${className}`}
    style={{
      boxShadow: soft
        ? "0 10px 28px -18px rgba(16,42,52,0.22)"
        : "0 26px 60px -28px rgba(16,42,52,0.38), 0 4px 12px -6px rgba(16,42,52,0.10)",
      ...style,
    }}
  >
    {children}
  </div>
);

const DocChip: React.FC<{
  label: string;
  tone: "teal" | "blue" | "green";
  className?: string;
  style?: React.CSSProperties;
}> = ({ label, tone, className = "", style }) => {
  const fill =
    tone === "teal" ? "#02857E" : tone === "blue" ? "#3B82F6" : "#22C55E";
  return (
    <Card className={`flex items-center gap-3 px-4 py-3.5 ${className}`} style={style}>
      <svg viewBox="0 0 24 24" className="h-9 w-9 shrink-0" aria-hidden>
        <path
          d="M6 2.5h8l4.5 4.5v14A1.5 1.5 0 0 1 17 22.5H6A1.5 1.5 0 0 1 4.5 21V4A1.5 1.5 0 0 1 6 2.5Z"
          fill={fill}
          fillOpacity="0.16"
        />
        <path d="M14 2.5 18.5 7H15.5A1.5 1.5 0 0 1 14 5.5V2.5Z" fill={fill} fillOpacity="0.4" />
        <rect x="8" y="11" width="7" height="1.6" rx="0.8" fill={fill} />
        <rect x="8" y="14.4" width="5" height="1.6" rx="0.8" fill={fill} fillOpacity="0.6" />
      </svg>
      <div>
        <div className="text-[15px] font-semibold leading-tight text-[#0E1B1A]">{label}</div>
        <div className="text-[12.5px] font-medium text-gray-400">PDF</div>
      </div>
    </Card>
  );
};

const Waveform: React.FC = () => (
  <svg viewBox="0 0 160 34" className="h-[34px] w-[160px]" aria-hidden>
    {Array.from({ length: 34 }).map((_, i) => {
      const h = [8, 14, 20, 11, 26, 17, 30, 12, 22, 9, 18, 28, 13, 24, 10, 19, 31, 15][i % 18];
      return (
        <rect
          key={i}
          x={i * 4.7}
          y={(34 - h) / 2}
          width="2"
          height={h}
          rx="1"
          fill="#02857E"
          fillOpacity={i < 20 ? 0.85 : 0.28}
        />
      );
    })}
  </svg>
);

const Collage: React.FC = () => (
  <div className="relative" style={{ width: 900, height: 800 }}>
    {/* dashed connector paths */}
    <svg
      viewBox="0 0 900 800"
      className="pointer-events-none absolute inset-0 h-full w-full"
      fill="none"
      aria-hidden
    >
      <g stroke="#02857E" strokeOpacity="0.32" strokeWidth="1.6" strokeDasharray="5 7">
        <path d="M300 380C210 470 300 560 430 545" />
        <path d="M470 300C620 250 700 190 705 120" />
        <path d="M560 640C650 700 760 690 810 630" />
        <path d="M250 300C180 250 190 190 250 160" />
      </g>
    </svg>

    {/* teal accent ticks (top right) */}
    <svg
      className="absolute"
      style={{ left: 782, top: 44, width: 70, height: 46 }}
      viewBox="0 0 70 46"
      fill="none"
      aria-hidden
    >
      <g stroke="#0FA88E" strokeWidth="4.5" strokeLinecap="round">
        <path d="M10 34 20 8" />
        <path d="M32 30 40 10" />
        <path d="M52 24 60 12" />
      </g>
    </svg>

    {/* teal phone bubble */}
    <div
      className="absolute flex items-center justify-center rounded-full"
      style={{
        left: 782,
        top: 500,
        height: 108,
        width: 108,
        background: "linear-gradient(150deg, #17E5DD 0%, #02857E 100%)",
        boxShadow: "0 24px 44px -20px rgba(2,133,126,0.55)",
      }}
    >
      <svg viewBox="0 0 24 24" className="h-12 w-12" fill="#fff" aria-hidden>
        <path d="M6.6 3.5a1.7 1.7 0 0 1 2.3.5l1.6 2.4a1.7 1.7 0 0 1-.3 2.2l-1 .9a11 11 0 0 0 4.9 4.9l.9-1a1.7 1.7 0 0 1 2.2-.3l2.4 1.6a1.7 1.7 0 0 1 .5 2.3l-1 1.6c-.6.9-1.7 1.3-2.7 1A18.6 18.6 0 0 1 4.1 7.6c-.3-1 .1-2.1 1-2.7l1.5-1.4Z" />
      </svg>
    </div>

    {/* blurred inbox skeleton (behind) */}
    <Card
      soft
      className="overflow-hidden"
      style={{ left: 20, top: 120, width: 400, height: 400, opacity: 0.85 }}
    >
      <div className="px-6 pt-6">
        <div className="flex items-center gap-3">
          <span className="h-3.5 w-3.5 rounded-full bg-[#17E5DD]" />
          <span className="h-2.5 w-24 rounded-full bg-[#E7EDF1]" />
        </div>
      </div>
      <div className="mt-6 space-y-7 px-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <span
              className={`h-4 w-4 rounded-full ${
                i === 0 ? "bg-[#2563EB]" : i === 1 ? "bg-[#E7EDF1]" : i === 2 ? "bg-[#0E1B1A]" : "bg-[#E7EDF1]"
              }`}
            />
            <span className="h-2.5 flex-1 rounded-full bg-[#EEF2F5]" />
            <span className="h-2.5 w-10 rounded-full bg-[#F3F6F8]" />
          </div>
        ))}
      </div>
      <div className="mt-8 space-y-7 px-6">
        {[0, 1].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <span className="h-2.5 flex-1 rounded-full bg-[#F1F5F7]" />
            <span className="h-2.5 w-8 rounded-full bg-[#F5F8FA]" />
          </div>
        ))}
      </div>
    </Card>

    {/* EMAIL card */}
    <Card style={{ left: 210, top: 10, width: 590 }}>
      <div className="flex items-center justify-between px-7 pt-6">
        <svg viewBox="0 0 24 24" className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span className="text-xl leading-none tracking-widest text-gray-300">···</span>
      </div>
      <div className="px-7 pb-7 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2563EB]">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#fff" aria-hidden>
                <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5v11A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-11Zm2.2.5L12 12.2 18.8 7H5.2Z" />
              </svg>
            </span>
            <span className="text-[22px] font-bold tracking-tight text-[#0E1B1A]">Email</span>
          </div>
          <span className="text-[15px] font-medium text-gray-400">9:41 AM</span>
        </div>
        <div className="mt-5 text-[26px] font-semibold leading-snug tracking-tight text-[#0E1B1A]">
          RE: RE: rate on the Bronx load
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-[19px] text-gray-500">
            Dallas, TX <span className="text-gray-400">→</span> Bronx, NY <span className="text-gray-300">·</span> Reefer
          </div>
          <svg viewBox="0 0 24 24" className="h-6 w-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
            <path d="M12 3.8l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 17.2 6.8 20l1-5.9L3.5 10l5.9-.8L12 3.8Z" />
          </svg>
        </div>
      </div>
    </Card>

    {/* TEXT MESSAGE card */}
    <Card style={{ left: 470, top: 250, width: 420 }}>
      <div className="px-6 pb-5 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#22C55E]">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#fff" aria-hidden>
                <path d="M12 4c-4.6 0-8 2.9-8 6.6 0 2 1 3.8 2.7 5-.2 1.2-.8 2.2-1.5 3 1.6-.2 3-.9 4-1.7 .9.2 1.8.3 2.8.3 4.6 0 8-2.9 8-6.6S16.6 4 12 4Z" />
              </svg>
            </span>
            <span className="text-[19px] font-bold tracking-tight text-[#0E1B1A]">Text message</span>
          </div>
          <span className="text-lg leading-none tracking-widest text-gray-300">···</span>
        </div>
        <div className="mt-5 text-center text-[13.5px] font-medium text-gray-400">Today 9:38 AM</div>
        <div className="mt-4 space-y-3">
          <div className="inline-block rounded-2xl bg-[#F1F4F6] px-5 py-3 text-[17px] text-[#0E1B1A]">
            $6,900 — can cover
          </div>
          <div className="inline-block rounded-2xl bg-[#F1F4F6] px-5 py-3 text-[17px] text-[#0E1B1A]">
            Pickup moved to 2 PM
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <span className="text-2xl font-light leading-none text-gray-400">+</span>
          <div className="flex flex-1 items-center justify-between rounded-full border border-[#E9EEF1] px-4 py-2.5">
            <span className="text-[15px] text-gray-400">Message</span>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#02857E]">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#fff" aria-hidden>
                <path d="M12 4l6 6h-4v8h-4v-8H6l6-6Z" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </Card>

    {/* VOICEMAIL card */}
    <Card style={{ left: 60, top: 470, width: 560 }}>
      <div className="px-7 pb-7 pt-6">
        <div className="flex items-center gap-3.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#6D5BE3]">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#fff" aria-hidden>
              <path d="M6.5 8a4 4 0 1 0 2.9 6.8h5.2A4 4 0 1 0 17.5 8a4 4 0 0 0-3.6 5.6h-3.8A4 4 0 0 0 6.5 8Z" />
            </svg>
          </span>
          <span className="text-[21px] font-bold tracking-tight text-[#0E1B1A]">Voicemail</span>
        </div>
        <div className="mt-5 flex items-center justify-between gap-5">
          <div>
            <div className="text-[22px] font-medium tracking-tight text-[#0E1B1A]">
              Broker · 1:07 — rate update
            </div>
            <div className="mt-2 text-[17px] text-gray-500">3 missed calls</div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4EBEE]">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="#02857E" aria-hidden>
                <path d="M8 5.5l10 6.5-10 6.5v-13Z" />
              </svg>
            </span>
            <Waveform />
            <span className="text-[14px] font-medium text-gray-400">1:07</span>
          </div>
        </div>
      </div>
    </Card>

    {/* DM card */}
    <Card style={{ left: 600, top: 620, width: 300 }}>
      <div className="rounded-[22px] bg-[#F4F9FB] px-5 pb-4 pt-5">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2563EB]">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="#fff" aria-hidden>
              <path d="M21 4L3 11l6 2.2L11 20l10-16Z" />
            </svg>
          </span>
          <span className="text-[19px] font-bold tracking-tight text-[#0E1B1A]">DM</span>
        </div>
        <div className="mt-3 text-[17px] leading-snug text-gray-600">
          Can you beat $6,800 on this lane?
        </div>
        <div className="mt-3 flex items-center justify-end gap-1.5 text-[13.5px] font-medium text-gray-400">
          Seen 9:40 AM
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="#02857E" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
            <path d="M3 13l4 4 7-9M12.5 17l7-9" />
          </svg>
        </div>
      </div>
    </Card>

    {/* document chips */}
    <DocChip label="Rate Conf" tone="teal" style={{ left: 0, top: 385, width: 208 }} />
    <DocChip label="POD" tone="blue" style={{ left: 100, top: 690, width: 190 }} />
    <DocChip label="BOL" tone="green" style={{ left: 330, top: 700, width: 190 }} />
  </div>
);

/* ── section ──────────────────────────────────────────────────── */

const HomeProblem: React.FC = () => (
  <section className="relative overflow-hidden bg-white pt-20 pb-20 sm:pt-24 sm:pb-24">
    {/* hero → white atmospheric transition */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-40"
      style={{
        background:
          "linear-gradient(180deg, rgba(8,29,38,0.10) 0%, rgba(0,207,196,0.06) 26%, rgba(255,255,255,0) 100%)",
      }}
    />

    <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
      {/* copy */}
      <div className="lg:order-2 lg:pl-6">
        <div className="text-[13px] font-semibold uppercase tracking-[0.24em] text-[#02857E]">
          The Problem
        </div>
        <div className="mt-3 h-[3px] w-14 rounded-full bg-[#02857E]" />

        <h2 className="mt-8 text-[42px] font-extrabold leading-[1.06] tracking-[-0.03em] text-[#0E1B1A] sm:text-[56px] lg:text-[64px]">
          Sourcing freight shouldn&apos;t take all day.
        </h2>

        <p className="mt-8 max-w-[34ch] text-[19px] leading-[1.65] text-gray-600 sm:text-[21px]">
          Calling the same three brokers. Rates buried in email threads. Quotes in text
          messages, updates in voicemails, documents scattered across inboxes. And no real way
          to know whether the rate you took was the right one.
        </p>

        <p className="mt-8 text-[19px] font-bold tracking-tight text-[#0E1B1A] sm:text-[21px]">
          There&apos;s a faster way to do this.
        </p>
      </div>

      {/* collage — fixed design canvas, scaled per breakpoint */}
      <div className="relative w-full lg:order-1">
        <div className="relative h-[320px] sm:h-[560px] lg:h-[600px] xl:h-[660px]">
          <div className="absolute left-0 top-0 origin-top-left scale-[0.37] sm:scale-[0.68] lg:scale-[0.55] xl:scale-[0.68] 2xl:scale-[0.75]">
            <Collage />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HomeProblem;
